import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import SimpleDataTable from '../SimpleDataTable';
import TreeTableButtons from '../TreeTableButtons';
import Section from '../Section';

class SimpleDataTableWithButtons extends React.Component {
  constructor(props) {
    super(props);
    const { treeTableButtonsProps, defaultExpLevel } = this.props;
    this.state = {
      activeButton: this.getInitialActiveButton(treeTableButtonsProps.buttons),
      expandLevel: defaultExpLevel
    };
  }

  getInitialActiveButton = (buttons) => {
    const { defaultExpLevel } = this.props;
    const activeButton = buttons.find((button) => defaultExpLevel === button.expLevel);
    return activeButton.accessor;
  }

  expandRows = (e, { level, accessor }) => {
    const { onClickCallback } = this.props;
    this.setState({
      activeButton: accessor,
      expandLevel: level
    });
    if (typeof onClickCallback === 'function') {
      onClickCallback({ level, accessor });
    }
  };

  renderSubComponent = (activeButton) => {
    const { simpleDataTableProps: { renderSubComponent } } = this.props;
    if (typeof renderSubComponent === 'function') {
      return renderSubComponent(activeButton);
    }
    if (typeof renderSubComponent === 'boolean') {
      return renderSubComponent;
    }
    return false;
  }

  render() {
    const { treeTableButtonsProps, simpleDataTableProps } = this.props;
    const { expandLevel, activeButton } = this.state;
    const tableProps = {
      ...simpleDataTableProps,
      expandLevel,
      activeButton,
      renderSubComponent: this.renderSubComponent(activeButton)
    };
    const { buttons, ...restButtonsProps } = treeTableButtonsProps;
    const buttonsProps = [];
    buttons.forEach((button) => {
      buttonsProps.push({
        text: button.text,
        accessor: button.accessor,
        usage: button.accessor === activeButton ? '' : 'secondary',
        info: button.info,
        onClick: (e) => this.expandRows(e, { level: button.expLevel, accessor: button.accessor })
      });
    });
    const buttonProps = {
      buttons: buttonsProps,
      ...restButtonsProps
    };
    return(
      <Fragment>
        <TreeTableButtons {...buttonProps} />
        <Section>
          <SimpleDataTable {...tableProps} />
        </Section>
      </Fragment>
    );
  }
}

SimpleDataTableWithButtons.propTypes = {
  defaultExpLevel: PropTypes.number,
  simpleDataTableProps: PropTypes.shape({
    maxColWidth: PropTypes.number,
    setMaxColWidth: PropTypes.bool,
    mantissa: PropTypes.number,
    download: PropTypes.string,
    className: PropTypes.string,
    pagination: PropTypes.bool,
    pageSize: PropTypes.number,
    table: PropTypes.object,
    columns: PropTypes.object,
    /** This function receive the row data for the subcomponent. Whatever it returns will be rendered
      * in a collapsable subrow of the table.
    * */
    getSubComponent: PropTypes.func,
    /** A boolean or a function that returns a boolean value that is used to determine whether to render SubComponent.
      * by default, if nothing is passed, SubComponent is not rendered.
    * */
    renderSubComponent: PropTypes.oneOfType([
      /** This function is a callback function that passes up the activeButton accessor state
        * and returns a boolean value to pass down to the SimpleDataTable component to determine whether to render SubComponent.
        * React Table SubComponent needs to receive a null value instead of a function which returns a null value to disable rendering SubComponent.
      * */
      PropTypes.func,
      /** If the SubComponent rendering condition doesn't need to access the activeButton accessor state,
        * simply pass true or false to control the rendering of SubComponent.
      * */
      PropTypes.bool
    ])
  }),
  treeTableButtonsProps: PropTypes.shape({
    /** The hidden title for a11y */
    a11yTitle: PropTypes.string.isRequired,
    /** The header level of the a11y header, default level 4. */
    headerLevel: PropTypes.number,
    /** An array of button that control the expand/collapse of tree table rows */
    buttons: PropTypes.arrayOf(PropTypes.shape({
      /** The button information */
      info: PropTypes.string,
      /** The text displayed on the buttons */
      text: PropTypes.string,
      /** The expand level the button should set on the tree table */
      expLevel: PropTypes.number,
      /** The data access of the field the button controls expansion of */
      accessor: PropTypes.string
    }))
  }),
  /** An OnClickCallback triggered on button click that tell you the level
      of expansion and the table access the button is interacting with.
   */
  onClickCallback: PropTypes.func
};

export default SimpleDataTableWithButtons;
